import { HardDrive } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { BackupStorageInventory } from "@shared/backup-storage-contract";

export function formatBackupBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${Number((bytes / 1024 ** index).toFixed(1))} ${units[index]}`;
}

/** Current retained files grouped by date, NOT reconstructed historical totals. */
export function retainedBackupMonths(
  volume: BackupStorageInventory["volumeByDate"],
  now: Date,
) {
  const months = Array.from({ length: 12 }, (_, index) => {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11 + index, 1));
    return {
      key: date.toISOString().slice(0, 7),
      label: date.toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }),
      bytes: 0,
    };
  });
  let olderBytes = 0;
  for (const point of volume) {
    const month = months.find(item => item.key === point.date.slice(0, 7));
    if (month) month.bytes += point.bytes;
    else if (point.date < months[0].key) olderBytes += point.bytes;
  }
  return olderBytes > 0
    ? [{ key: "older", label: `Before ${months[0].label}`, bytes: olderBytes }, ...months]
    : months;
}

export function BackupStorageCard({ inventory, loading = false }: {
  inventory?: BackupStorageInventory;
  loading?: boolean;
}) {
  const available = inventory?.totalBytes != null && inventory.status !== "unavailable";
  const months = retainedBackupMonths(inventory?.volumeByDate ?? [], new Date());
  const max = Math.max(1, ...months.map(month => month.bytes));
  const categories = inventory?.categories;
  const dated = months.some(month => month.bytes > 0);
  return (
    <Card className="navy-card border-gray-600">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
          <HardDrive className="h-4 w-4" />
          Backup Storage
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-white" data-testid="backup-storage-total">
          {available ? formatBackupBytes(inventory.totalBytes!) : loading ? "Loading…" : "Unavailable"}
        </div>
        {available && categories ? (
          <>
            <p className="text-[10px] text-gray-400 mt-1">
              DB {formatBackupBytes(categories.database.bytes)} · HTML {formatBackupBytes(categories.html.bytes)} · ZIP {formatBackupBytes(categories.zip.bytes)}
            </p>
            <p className="text-[10px] text-gray-500">{inventory.totalFiles} retained files in Dropbox</p>
            <div className="mt-3">
              <p className="text-[10px] text-gray-400 mb-1">Retained volume by backup month</p>
              {dated ? (
                <>
                  <div
                    role="img"
                    aria-label={`Retained backup volume: ${months.map(month => `${month.label}: ${formatBackupBytes(month.bytes)}`).join("; ")}`}
                    className="h-10 flex items-end gap-1"
                  >
                    {months.map(month => (
                      <div
                        key={month.key}
                        title={`${month.label}: ${formatBackupBytes(month.bytes)}`}
                        className="flex-1 min-w-0 rounded-t-sm bg-sky-400/60"
                        style={{ height: `${Math.max(month.bytes > 0 ? 3 : 1, month.bytes / max * 100)}%` }}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between text-[9px] text-gray-500 mt-1" aria-hidden="true">
                    <span>{months[0].key === "older" ? "Older" : months[0].label}</span>
                    <span>{months[months.length - 1].label}</span>
                  </div>
                </>
              ) : <p className="text-[10px] text-gray-500">No dated backup files.</p>}
              <p className="text-[9px] text-gray-500 mt-1">
                Current files only; deleted backups and live attachments excluded.
                {!!inventory.undatedBytes && ` Undated: ${formatBackupBytes(inventory.undatedBytes)}.`}
              </p>
            </div>
          </>
        ) : (
          <p className="text-xs text-gray-500 mt-1">
            {loading ? "Checking retained Dropbox backups." : "Dropbox backup size could not be verified."}
          </p>
        )}
        {inventory?.status === "stale" && (
          <p role="status" className="text-[10px] text-amber-400 mt-1">Showing the last successful scan; refresh unavailable.</p>
        )}
        {inventory?.scannedAt && available && (
          <p className="text-[9px] text-gray-500 mt-1">
            Checked {new Date(inventory.scannedAt).toLocaleString()} · cached up to 10 min
          </p>
        )}
      </CardContent>
    </Card>
  );
}